import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react';

import {
  useCreateCategory,
  useCreatePerson,
  useCreateStatus,
  useCreateStream,
  useCreateTeam,
  useDeleteCategory,
  useDeletePerson,
  useDeleteStatus,
  useDeleteStream,
  useDeleteTeam,
  usePatchCategory,
  usePatchPerson,
  usePatchStatus,
  usePatchStream,
  usePatchTeam,
  useSetDictionaryPositions,
} from '../api/planning-hooks';
import type { PatchPersonBody } from '../api/planning';
import { errorMessage, fieldIssues, isConflict } from '../api/planning-hooks';
import type {
  Category,
  DictionaryKind,
  Person,
  RegistryDocument,
  RegistryStatus,
  Stream,
  Team,
} from '../api/types';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { FilterBar } from '../components/FilterBar';
import { CreateButton, PageHeader } from '../components/view-parts';
import { Badge, Button, EmptyState, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { useQueryParams } from '../lib/query-params';
import { personWord, projectWord } from '../lib/plural';

type Section = 'people' | 'teams' | 'streams' | 'categories' | 'statuses';

const SECTIONS: Array<{ key: Section; label: string }> = [
  { key: 'people', label: 'Люди' },
  { key: 'teams', label: 'Команды' },
  { key: 'streams', label: 'Потоки' },
  { key: 'categories', label: 'Категории' },
  // The status dictionary (F-01/F-23, 2026-10-14) — last, after Категории.
  { key: 'statuses', label: 'Статусы' },
];

/**
 * F-23: dictionaries. People are editable (name, role, email, team, active —
 * F-12), teams/streams/categories too. Deleting is confirmed with the cascade
 * spelled out: removing a person removes their assignments (and audits it).
 */
export function SettingsPage(): ReactElement {
  return <RegistryGate label="Загрузка справочников">{(registry) => <SettingsView registry={registry} />}</RegistryGate>;
}

function SettingsView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { params } = useQueryParams();
  const section: Section = SECTIONS.some((item) => item.key === params.section) ? (params.section as Section) : 'people';

  return (
    <>
      <PageHeader title="Настройки" sub="Справочники реестра. Переименования не рвут ссылки — id стабильны (F-12)." />

      <SummaryChips />

      <FilterBar />

      {/* Section navigation as visible links (F-23, owner feedback 2026-10-26):
          the sections used to hide behind a «Раздел» select in the filter bar
          and team management could not be found. Real links — shareable URLs,
          other filter params are preserved. Default section keeps the
          canonical URL (no `section` key). */}
      <nav className="section-tabs" aria-label="Разделы настроек">
        {SECTIONS.map((item) => (
          <Link
            key={item.key}
            to={sectionTarget(params, item.key)}
            className={item.key === section ? 'section-tabs__link section-tabs__link--active' : 'section-tabs__link'}
            aria-current={item.key === section ? 'page' : undefined}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      {section === 'people' ? <PeopleSection registry={registry} /> : null}
      {section === 'teams' ? <TeamsSection registry={registry} /> : null}
      {section === 'streams' ? <StreamsSection registry={registry} /> : null}
      {section === 'categories' ? <CategoriesSection registry={registry} /> : null}
      {section === 'statuses' ? <StatusesSection registry={registry} /> : null}
    </>
  );
}

/** URL of a settings section with every other query param preserved. */
function sectionTarget(params: Record<string, string>, key: Section): string {
  const next = new URLSearchParams(params);
  if (key === 'people') next.delete('section');
  else next.set('section', key);
  const query = next.toString();
  return query === '' ? '/settings' : `/settings?${query}`;
}

// -- Manual dictionary order (↑/↓ via /registry/dictionary-positions) --------

/**
 * One ↑/↓ pair. A click sends the dictionary's WHOLE id list with the row
 * moved by one — the shared full-list contract
 * (`POST /registry/dictionary-positions`): the server reorders, audits one
 * `order` record per moved entry and answers the fresh document, which swaps
 * into the cache. Disabled at the edges and while a reorder is in flight.
 */
function OrderButtons({
  kind,
  orderedIds,
  index,
  version,
  disabled,
}: {
  kind: DictionaryKind;
  /** The dictionary's CURRENT full id order (array order = display order). */
  orderedIds: string[];
  index: number;
  /** The document version this order was read at (the CAS token). */
  version: number;
  disabled?: boolean;
}): ReactElement {
  const reorder = useSetDictionaryPositions();
  const busy = reorder.isPending;
  const move = (to: number) => {
    if (to < 0 || to >= orderedIds.length) return;
    const next = [...orderedIds];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    reorder.mutate({ version, kind, orderedIds: next });
  };

  const title = (direction: 'up' | 'down') =>
    `Сдвинуть ${direction === 'up' ? 'выше' : 'ниже'}`;
  return (
    <span className="order-buttons">
      <button
        type="button"
        className="icon-btn icon-btn--sm"
        aria-label={`${title('up')} (позиция ${index + 1})`}
        title={title('up')}
        disabled={disabled || busy || index === 0}
        onClick={() => move(index - 1)}
      >
        <ArrowUp size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="icon-btn icon-btn--sm"
        aria-label={`${title('down')} (позиция ${index + 1})`}
        title={title('down')}
        disabled={disabled || busy || index === orderedIds.length - 1}
        onClick={() => move(index + 1)}
      >
        <ArrowDown size={14} aria-hidden="true" />
      </button>
    </span>
  );
}

// -- People -----------------------------------------------------------------

function PeopleSection({ registry }: { registry: RegistryDocument }): ReactElement {
  const [editing, setEditing] = useState<Person | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Person | null>(null);
  const remove = useDeletePerson();
  const teamById = new Map(registry.teams.map((team) => [team.id, team.name]));

  const assignmentsOf = (personId: string) =>
    registry.projects.filter((project) => project.assignments.some((a) => a.personId === personId));

  return (
    <section className="card card--flush">
      <header className="card-toolbar">
        <h2 className="card__title">Люди — {registry.people.length}</h2>
        <CreateButton label="Новый сотрудник" onClick={() => setEditing(null)} />
      </header>

      {registry.people.length === 0 ? (
        <EmptyState title="Справочник людей пуст" hint="Добавьте первого сотрудника." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <caption className="sr-only">Справочник людей</caption>
            <thead>
              <tr>
                <th scope="col">Имя</th>
                <th scope="col">Роль</th>
                <th scope="col">E-mail</th>
                <th scope="col">Команда</th>
                <th scope="col">Проектов</th>
                <th scope="col" className="num" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {registry.people.map((person) => (
                <tr key={person.id}>
                  <td className="cell-primary">
                    <span className="cell-primary__text">{person.name}</span>
                    {person.active ? null : (
                      <span className="cell-sub">
                        <Badge tone="warn">неактивен</Badge>
                      </span>
                    )}
                  </td>
                  <td className="cell-muted">{person.role || '—'}</td>
                  <td className="cell-muted">{person.email || '—'}</td>
                  <td>{teamById.get(person.teamId) ?? person.teamId}</td>
                  <td className="num">{assignmentsOf(person.id).length}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm"
                        aria-label={`Редактировать: ${person.name}`}
                        title="Редактировать"
                        onClick={() => setEditing(person)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm icon-btn--danger"
                        aria-label={`Удалить: ${person.name}`}
                        title="Удалить"
                        onClick={() => setDeleting(person)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined ? (
        <PersonModal open onClose={() => setEditing(undefined)} registry={registry} person={editing} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить сотрудника?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(
            { id: deleting.id, version: registry.version },
            { onSuccess: () => setDeleting(null) },
          );
        }}
        description={
          <p>
            {deleting ? `«${deleting.name}» будет удалён из справочника. ` : ''}
            Каскад: его назначения ({deleting ? assignmentsOf(deleting.id).length : 0}{' '}
            {projectWord(deleting ? assignmentsOf(deleting.id).length : 0)}) будут убраны, каждая правка попадёт в
            аудит-лог.
          </p>
        }
      />
    </section>
  );
}

function PersonModal({
  open,
  onClose,
  registry,
  person,
}: {
  open: boolean;
  onClose: () => void;
  registry: RegistryDocument;
  person: Person | null;
}): ReactElement {
  const create = useCreatePerson();
  const patch = usePatchPerson();
  const [name, setName] = useState(person?.name ?? '');
  const [role, setRole] = useState(person?.role ?? '');
  const [email, setEmail] = useState(person?.email ?? '');
  const [teamId, setTeamId] = useState(person?.teamId ?? registry.teams[0]?.id ?? '');
  const [notes, setNotes] = useState(person?.notes ?? '');
  const [active, setActive] = useState(person?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending || patch.isPending;
  const mutationError = (create.error ?? patch.error) as Error | null;
  const conflict = isConflict(mutationError);
  const issueOf = (field: string): string | undefined => fieldIssues(mutationError).get(field);

  const save = () => {
    if (name.trim() === '') {
      setError('Имя обязательно');
      return;
    }
    // Text fields always travel as strings: sending '' is how a field is
    // CLEARED (an absent key means "untouched" — arch §3.2).
    const fields = {
      name: name.trim(),
      role: role.trim(),
      email: email.trim(),
      teamId,
      notes: notes.trim(),
      active,
    };
    if (person) {
      const set: Partial<Omit<PatchPersonBody, 'version'>> = {};
      for (const [key, value] of Object.entries(fields)) {
        if ((person as unknown as Record<string, unknown>)[key] !== value) {
          (set as Record<string, unknown>)[key] = value;
        }
      }
      if (Object.keys(set).length === 0) return onClose();
      // Dictionary patches are FLAT: {version, …fields} — no `set` wrapper.
      patch.mutate({ id: person.id, body: { version: registry.version, ...set } }, { onSuccess: onClose });
    } else {
      create.mutate({ version: registry.version, ...fields }, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={person ? 'Редактировать сотрудника' : 'Новый сотрудник'}
      description="Переименование не рвёт ссылки на проекты (F-12). Смена команды сохраняет личную очередь."
      footer={
        <>
          {conflict ? (
            <span className="badge badge--warn" title={mutationError?.message}>
              Изменён кем-то другим — данные обновлены
            </span>
          ) : mutationError ? (
            <span className="badge badge--bad" role="alert" title={mutationError.message}>
              Не сохранено
            </span>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" onClick={save} loading={busy}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <div className="form-grid__full">
          <Field label="Имя" htmlFor="person-name" required error={error ?? issueOf('name')}>
            <Input
              id="person-name"
              value={name}
              aria-invalid={error !== null || issueOf('name') !== undefined ? true : undefined}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Роль" htmlFor="person-role">
          <Input id="person-role" value={role} onChange={(event) => setRole(event.target.value)} />
        </Field>
        <Field label="E-mail" htmlFor="person-email">
          <Input id="person-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <div className="form-grid__full">
          <Field label="Команда" htmlFor="person-team" hint="Человек входит ровно в одну команду" error={issueOf('teamId')}>
            <Select id="person-team" value={teamId} onChange={(event) => setTeamId(event.target.value)}>
              {registry.teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="form-grid__full">
          <Field label="Заметки" htmlFor="person-notes">
            <Textarea id="person-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </div>
        <div className="form-grid__full">
          <label className="check-field" htmlFor="person-active">
            <input
              id="person-active"
              type="checkbox"
              className="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            Активен
          </label>
        </div>
      </div>
    </Modal>
  );
}

// -- Teams ------------------------------------------------------------------

function TeamsSection({ registry }: { registry: RegistryDocument }): ReactElement {
  const [editing, setEditing] = useState<Team | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Team | null>(null);
  const remove = useDeleteTeam();
  const peopleIn = (teamId: string) => registry.people.filter((person) => person.teamId === teamId).length;

  return (
    <section className="card card--flush">
      <header className="card-toolbar">
        <h2 className="card__title">Команды — {registry.teams.length}</h2>
        <CreateButton label="Новая команда" onClick={() => setEditing(null)} />
      </header>

      {registry.teams.length === 0 ? (
        <EmptyState title="Команд нет" hint="Добавьте первую команду." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <caption className="sr-only">Справочник команд</caption>
            <thead>
              <tr>
                <th scope="col">Название</th>
                <th scope="col">Описание</th>
                <th scope="col" className="num">
                  Участников
                </th>
                <th scope="col" className="num" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {registry.teams.map((team) => (
                <tr key={team.id}>
                  <td className="cell-primary">{team.name}</td>
                  <td className="cell-muted">{team.description || '—'}</td>
                  <td className="num">{registry.people.filter((person) => person.teamId === team.id).length}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm"
                        aria-label={`Редактировать: ${team.name}`}
                        title="Редактировать"
                        onClick={() => setEditing(team)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm icon-btn--danger"
                        aria-label={`Удалить: ${team.name}`}
                        title="Удалить"
                        onClick={() => setDeleting(team)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined ? (
        <NameModal
          open
          onClose={() => setEditing(undefined)}
          registry={registry}
          kind="team"
          entity={editing}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить команду?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate({ id: deleting.id, version: registry.version }, { onSuccess: () => setDeleting(null) });
        }}
        description={
          <p>
            {deleting ? `«${deleting.name}» будет удалена. ` : ''}
            Удаление отклонится, пока в команде люди — сейчас это{' '}
            {deleting ? peopleIn(deleting.id) : 0} {personWord(deleting ? peopleIn(deleting.id) : 0)}. Сначала
            переведите их в другие команды (Настройки → Люди).
          </p>
        }
      />
    </section>
  );
}

// -- Streams + categories ---------------------------------------------------

function StreamsSection({ registry }: { registry: RegistryDocument }): ReactElement {
  const [editing, setEditing] = useState<Stream | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Stream | null>(null);
  const remove = useDeleteStream();
  const projectsIn = (streamId: string) => registry.projects.filter((project) => project.streamId === streamId).length;
  const personById = new Map(registry.people.map((person) => [person.id, person]));
  const streamIds = registry.streams.map((stream) => stream.id);

  return (
    <section className="card card--flush">
      <header className="card-toolbar">
        <h2 className="card__title">Потоки — {registry.streams.length}</h2>
        <CreateButton label="Новый поток" onClick={() => setEditing(null)} />
      </header>

      {registry.streams.length === 0 ? (
        <EmptyState title="Потоков нет" hint="Добавьте первый поток." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <caption className="sr-only">Справочник потоков</caption>
            <thead>
              <tr>
                <th scope="col" className="num" aria-label="Порядок" />
                <th scope="col">Название</th>
                <th scope="col">Описание</th>
                <th scope="col">Ответственный</th>
                <th scope="col" className="num">
                  Проектов
                </th>
                <th scope="col" className="num" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {registry.streams.map((stream, index) => (
                <tr key={stream.id}>
                  <td className="num">
                    <OrderButtons kind="streams" orderedIds={streamIds} index={index} version={registry.version} />
                  </td>
                  <td className="cell-primary">{stream.name}</td>
                  <td className="cell-muted">{stream.description || '—'}</td>
                  {/* «Ответственный» (2026-10-14): set in the flow header on
                      /streams or in the modal below; «—» = nobody. */}
                  <td className="cell-muted">{stream.ownerPersonId ? personById.get(stream.ownerPersonId)?.name ?? stream.ownerPersonId : '—'}</td>
                  <td className="num">{projectsIn(stream.id)}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm"
                        aria-label={`Редактировать: ${stream.name}`}
                        title="Редактировать"
                        onClick={() => setEditing(stream)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm icon-btn--danger"
                        aria-label={`Удалить: ${stream.name}`}
                        title="Удалить"
                        onClick={() => setDeleting(stream)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined ? (
        <NameModal open onClose={() => setEditing(undefined)} registry={registry} kind="stream" entity={editing} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить поток?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate({ id: deleting.id, version: registry.version }, { onSuccess: () => setDeleting(null) });
        }}
        description={
          <p>
            {deleting ? `«${deleting.name}» будет удалён. ` : ''}
            Удаление отклонится, пока на поток ссылается хотя бы один проект — сейчас их{' '}
            {deleting ? projectsIn(deleting.id) : 0}. Сначала перенесите проекты в другой поток.
          </p>
        }
      />
    </section>
  );
}

function CategoriesSection({ registry }: { registry: RegistryDocument }): ReactElement {
  const [editing, setEditing] = useState<Category | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const remove = useDeleteCategory();
  const projectsIn = (categoryId: string) => registry.projects.filter((project) => project.categoryId === categoryId).length;
  const categoryIds = registry.categories.map((category) => category.id);

  return (
    <section className="card card--flush">
      <header className="card-toolbar">
        <h2 className="card__title">Категории — {registry.categories.length}</h2>
        <CreateButton label="Новая категория" onClick={() => setEditing(null)} />
      </header>

      {registry.categories.length === 0 ? (
        <EmptyState title="Категорий нет" hint="Категория проекта выбирается только из этого словаря (F-06)." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <caption className="sr-only">Словарь категорий</caption>
            <thead>
              <tr>
                <th scope="col" className="num" aria-label="Порядок" />
                <th scope="col">Название</th>
                <th scope="col" className="num">
                  Проектов
                </th>
                <th scope="col" className="num" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {registry.categories.map((category, index) => (
                <tr key={category.id}>
                  <td className="num">
                    <OrderButtons kind="categories" orderedIds={categoryIds} index={index} version={registry.version} />
                  </td>
                  <td className="cell-primary">{category.name}</td>
                  <td className="num">{projectsIn(category.id)}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm"
                        aria-label={`Редактировать: ${category.name}`}
                        title="Редактировать"
                        onClick={() => setEditing(category)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm icon-btn--danger"
                        aria-label={`Удалить: ${category.name}`}
                        title="Удалить"
                        onClick={() => setDeleting(category)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined ? (
        <NameModal open onClose={() => setEditing(undefined)} registry={registry} kind="category" entity={editing} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить категорию?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate({ id: deleting.id, version: registry.version }, { onSuccess: () => setDeleting(null) });
        }}
        description={
          <p>
            {deleting ? `«${deleting.name}» будет удалена. ` : ''}
            Каскад: у {deleting ? projectsIn(deleting.id) : 0} {projectWord(deleting ? projectsIn(deleting.id) : 0)}{' '}
            категория будет снята.
          </p>
        }
      />
    </section>
  );
}

// -- Statuses (the managed dictionary, F-01/F-23) ----------------------------

/**
 * The status dictionary (2026-10-14): seven built-ins seeded `system: true` —
 * renamable and reorderable, NEVER deletable (row tints, summary chips and
 * overdue/support semantics key off their ids) — plus custom ones, deletable
 * only while no project references them. ARRAY ORDER is the display order of
 * every filter, select and status sort.
 */
function StatusesSection({ registry }: { registry: RegistryDocument }): ReactElement {
  const [editing, setEditing] = useState<RegistryStatus | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<RegistryStatus | null>(null);
  const remove = useDeleteStatus();
  const projectsIn = (statusId: string) => registry.projects.filter((project) => project.status === statusId).length;
  const statusIds = registry.statuses.map((status) => status.id);

  return (
    <section className="card card--flush">
      <header className="card-toolbar">
        <h2 className="card__title">Статусы — {registry.statuses.length}</h2>
        <CreateButton label="Новый статус" onClick={() => setEditing(null)} />
      </header>

      {registry.statuses.length === 0 ? (
        <EmptyState title="Статусов нет" hint="Добавьте первый статус." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <caption className="sr-only">Словарь статусов</caption>
            <thead>
              <tr>
                <th scope="col" className="num" aria-label="Порядок" />
                <th scope="col">Название</th>
                <th scope="col" className="num">
                  Проектов
                </th>
                <th scope="col" className="num" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {registry.statuses.map((status, index) => (
                <tr key={status.id}>
                  <td className="num">
                    <OrderButtons kind="statuses" orderedIds={statusIds} index={index} version={registry.version} />
                  </td>
                  <td className="cell-primary">
                    <span className="cell-primary__row">
                      <span className="cell-primary__text">{status.label}</span>
                      {status.system ? <Badge title="Системный статус — переименовать можно, удалить нельзя">системный</Badge> : null}
                    </span>
                  </td>
                  <td className="num">{projectsIn(status.id)}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--sm"
                        aria-label={`Переименовать: ${status.label}`}
                        title="Переименовать"
                        onClick={() => setEditing(status)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      {/* A system status has NO delete button at all — the
                          refusal (422) is a server guard, the UI simply does
                          not offer the action. */}
                      {status.system ? null : (
                        <button
                          type="button"
                          className="icon-btn icon-btn--sm icon-btn--danger"
                          aria-label={`Удалить: ${status.label}`}
                          title="Удалить"
                          onClick={() => setDeleting(status)}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined ? (
        <NameModal
          open
          onClose={() => setEditing(undefined)}
          registry={registry}
          kind="status"
          // A status entity is {id, label}: the shared modal speaks `name`.
          entity={editing === null ? null : { id: editing.id, name: editing.label }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить статус?"
        busy={remove.isPending}
        error={errorMessage(remove.error)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate({ id: deleting.id, version: registry.version }, { onSuccess: () => setDeleting(null) });
        }}
        description={
          <p>
            {deleting ? `«${deleting.label}» будет удалён из словаря. ` : ''}
            Каскад: удаление отклонится (422), пока статус используется — сейчас это{' '}
            {deleting ? projectsIn(deleting.id) : 0} {projectWord(deleting ? projectsIn(deleting.id) : 0)}. Сначала
            переведите проекты в другой статус.
          </p>
        }
      />
    </section>
  );
}

// -- Shared name/description modal -------------------------------------------

interface NameModalEntity {
  id: string;
  name: string;
  description?: string;
  /** Streams only: the responsible person («Ответственный»), `null` = none. */
  ownerPersonId?: string | null;
}

interface NameModalProps {
  open: boolean;
  onClose: () => void;
  registry: RegistryDocument;
  kind: 'team' | 'stream' | 'category' | 'status';
  entity: NameModalEntity | null;
}

function NameModal({ open, onClose, registry, kind, entity }: NameModalProps): ReactElement {
  const createTeam = useCreateTeam();
  const patchTeam = usePatchTeam();
  const createStream = useCreateStream();
  const patchStream = usePatchStream();
  const createCategory = useCreateCategory();
  const patchCategory = usePatchCategory();
  const createStatus = useCreateStatus();
  const patchStatus = usePatchStatus();

  const titles = {
    team: { noun: 'команды', create: 'Новая команда', edit: 'Редактировать команду', withDescription: true, withOwner: false },
    stream: { noun: 'потока', create: 'Новый поток', edit: 'Редактировать поток', withDescription: true, withOwner: true },
    category: { noun: 'категории', create: 'Новая категория', edit: 'Редактировать категорию', withDescription: false, withOwner: false },
    // A status card is the LABEL only: id and `system` are server-owned.
    status: { noun: 'статуса', create: 'Новый статус', edit: 'Переименовать статус', withDescription: false, withOwner: false },
  }[kind];

  const [name, setName] = useState(entity?.name ?? '');
  const [description, setDescription] = useState(entity?.description ?? '');
  // «Ответственный»: '' stands for "nobody" (a select cannot carry undefined).
  const [ownerPersonId, setOwnerPersonId] = useState(entity?.ownerPersonId ?? '');
  const [error, setError] = useState<string | null>(null);

  const creating = entity === null;
  const busy =
    createTeam.isPending ||
    patchTeam.isPending ||
    createStream.isPending ||
    patchStream.isPending ||
    createCategory.isPending ||
    patchCategory.isPending ||
    createStatus.isPending ||
    patchStatus.isPending;
  const mutationError = (createTeam.error ?? patchTeam.error ?? createStream.error ?? patchStream.error ?? createCategory.error ?? patchCategory.error ?? createStatus.error ?? patchStatus.error) as Error | null;
  const conflict = isConflict(mutationError);
  const issueOf = (field: string): string | undefined => fieldIssues(mutationError).get(field);

  const save = () => {
    if (name.trim() === '') {
      setError('Название обязательно');
      return;
    }
    // Dictionary bodies are FLAT on the wire (arch §3.2): {version, …fields}.
    // `description: ''` CLEARS the field — an absent/undefined key would mean
    // "untouched".
    const version = registry.version;
    if (kind === 'team') {
      const payload = { name: name.trim(), description: description.trim() };
      if (creating) createTeam.mutate({ version, ...payload }, { onSuccess: onClose });
      else if (entity) patchTeam.mutate({ id: entity.id, body: { version, ...payload } }, { onSuccess: onClose });
    } else if (kind === 'stream') {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        ownerPersonId: ownerPersonId === '' ? null : ownerPersonId,
      };
      if (creating) createStream.mutate({ version, ...payload }, { onSuccess: onClose });
      else if (entity) patchStream.mutate({ id: entity.id, body: { version, ...payload } }, { onSuccess: onClose });
    } else if (kind === 'status') {
      const payload = { label: name.trim() };
      if (creating) createStatus.mutate({ version, ...payload }, { onSuccess: onClose });
      else if (entity) patchStatus.mutate({ id: entity.id, body: { version, ...payload } }, { onSuccess: onClose });
    } else {
      const payload = { name: name.trim() };
      if (creating) createCategory.mutate({ version, ...payload }, { onSuccess: onClose });
      else if (entity) patchCategory.mutate({ id: entity.id, body: { version, ...payload } }, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={creating ? titles.create : titles.edit}
      description={`id стабилен: переименование ${titles.noun} не ломает ссылки.`}
      footer={
        <>
          {conflict ? (
            <span className="badge badge--warn" title={mutationError?.message}>
              Изменён кем-то другим — данные обновлены
            </span>
          ) : mutationError ? (
            <span className="badge badge--bad" role="alert" title={mutationError.message}>
              Не сохранено
            </span>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" onClick={save} loading={busy}>
            Сохранить
          </Button>
        </>
      }
    >
      <Field
        label={kind === 'status' ? 'Название статуса' : 'Название'}
        htmlFor={`${kind}-name`}
        required
        error={error ?? issueOf('name') ?? issueOf('label')}
      >
        <Input
          id={`${kind}-name`}
          value={name}
          aria-invalid={error !== null || issueOf('name') !== undefined || issueOf('label') !== undefined ? true : undefined}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      {titles.withDescription ? (
        <Field label="Описание" htmlFor={`${kind}-description`}>
          <Textarea
            id={`${kind}-description`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      ) : null}
      {titles.withOwner ? (
        <Field label="Ответственный" htmlFor={`${kind}-owner`} hint="Поток в шапке вида «По потокам» тоже можно назначить здесь" error={issueOf('ownerPersonId')}>
          <Select
            id={`${kind}-owner`}
            value={ownerPersonId}
            onChange={(event) => setOwnerPersonId(event.target.value)}
          >
            <option value="">—</option>
            {registry.people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.active ? person.name : `${person.name} (неактивен)`}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
    </Modal>
  );
}
